import { SingleNodeAudioTransformDevice, type Device } from "amazon-chime-sdk-js";

// Frecuencias DTMF (fila, columna) en Hz.
export const FREQS: Record<string, [number, number]> = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
  "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
  "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
  "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};

const TONE_MS = 120;
const GAP_MS = 80;

/**
 * Micrófono con tonos DTMF mezclados en el audio que sale hacia Connect
 * (DTMF "en banda"). El nodo va después de la cancelación de eco y la
 * supresión de ruido del navegador, así que los tonos no se filtran.
 */
export class ToneMicDevice extends SingleNodeAudioTransformDevice<GainNode> {
  private context: AudioContext | undefined;
  private queue: Promise<void> = Promise.resolve();

  async createSingleAudioNode(context: AudioContext): Promise<GainNode> {
    this.context = context;
    return context.createGain(); // paso directo del micrófono; los tonos se suman aquí
  }

  /** Encola un dígito; los tonos se reproducen de uno en uno, nunca solapados. */
  play(digit: string): Promise<void> {
    this.queue = this.queue.then(() => this.tone(digit)).catch(() => {});
    return this.queue;
  }

  private tone(digit: string): Promise<void> {
    const freqs = FREQS[digit];
    const ctx = this.context;
    const node = this.node; // leer siempre el nodo actual: el SDK lo recrea al reaplicar el dispositivo
    if (!freqs || !ctx || !node) return Promise.resolve();

    const start = ctx.currentTime + 0.01;
    const end = start + TONE_MS / 1000;
    const level = ctx.createGain();
    level.gain.setValueAtTime(0, start);
    level.gain.linearRampToValueAtTime(0.25, start + 0.005); // rampas cortas para evitar clics
    level.gain.setValueAtTime(0.25, end - 0.005);
    level.gain.linearRampToValueAtTime(0, end);
    level.connect(node);

    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.frequency.value = f;
      osc.connect(level);
      osc.start(start);
      osc.stop(end);
    }
    return new Promise((resolve) =>
      setTimeout(() => {
        level.disconnect();
        resolve();
      }, TONE_MS + GAP_MS + 10),
    );
  }
}

export function isDtmfDigit(key: string): boolean {
  return key in FREQS;
}

export type { Device };
